<template>
  <v-bottom-navigation class="d-md-none bottom-nav" :height="bottomNavHeight">
    <nav>
      <v-list
        class="justify-center d-flex flex-row bg-transparent py-0 toolbar-links text-secondary"
        variant="plain"
        tag="ul"
        :aria-label="t(l.label_main_navigation)"
        role="list"
      >
        <li v-for="tab in tabs" :key="tab.path" v-show="!tab.disabled">
          <v-list-item :to="tab.path" active-class="active-nav-link" class="h-100">
            <v-icon color="brand" class="text-secondary" size="24"> {{ tab.icon }} </v-icon>
            <v-list-item-title :class="capitalizationClass" class="tab-text"> {{ t(tab.label) }}</v-list-item-title>
          </v-list-item>
        </li>
      </v-list>
    </nav>
  </v-bottom-navigation>
</template>

<script setup lang="ts">
import config from '@/config/ConfigProvider';
import { useLocale, useLocalisation } from '@/plugins/translation';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const showGuides = ref<string>(config.value('showGuides'));
const showMaterials = ref<string>(config.value('showMaterials'));
const bottomNavHeight = computed(() => {
  let height = 64;
  if (window.matchMedia('(display-mode: standalone)').matches) {
    height += 12;
  }
  return height;
});

const { t, l } = useLocale();
const capitalizationClass = computed(() => {
  const { language } = useLocalisation();
  return language.value === 'en-GB' ? 'text-capitalize' : 'text-capitalize-first';
});
const tabs = [
  { path: '/videos', icon: 'custom:videoIcon', label: l.tabs.films, disabled: false },
  { path: '/guides', icon: 'custom:guideIcon', label: l.tabs.guides_short, disabled: !showGuides.value },
  { path: '/materials', icon: 'mdi-file-document-outline', label: l.tabs.materials, disabled: !showMaterials.value },
];
const activeTab = ref<number>(0);
const route = useRoute();
watch(
  route,
  (newRoute) => {
    const tab = tabs.findIndex((t) => newRoute.path.startsWith(t.path));
    if (tab !== -1) {
      activeTab.value = tab;
    }
  },
  { immediate: true },
);
</script>
<style scoped lang="scss">
@use '@/foundations/theme/settings';
.bottom-nav {
  padding: 12px 16px 0;
  z-index: 10;
}
:deep(.toolbar-links) {
  .v-list-item__spacer {
    display: none;
  }
  .v-list-item {
    opacity: 1;
    width: 90px;
    padding: 0 16px;
    .v-icon,
    .v-list-item__title {
      opacity: 1;
    }
    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
      color: var(--v-theme-primary);
    }
    .v-list-item__content {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }
  .active-nav-link {
    $active-link-border-width: 2px;
    border-bottom-width: $active-link-border-width;
    color: var(--v-theme-primary);
    border-bottom-color: var(--v-theme-primary);
    .v-list-item__prepend,
    .v-list-item__content {
      margin-bottom: calc($active-link-border-width * -1); // trick to not make the text bounce
    }
  }
}

.tab-text {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.0892857143em;
}

.text-capitalize-first {
  text-transform: lowercase !important;
}

.text-capitalize-first::first-letter {
  text-transform: uppercase !important;
}
</style>
